import React, { useMemo } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { Text, Box, Flex, MUIModal, Formik, ButtonFilled, ButtonText } from 'Components/Base';
import { FeedbackButtonOption, FeedbackRating, FeedbackComplete } from 'Components/Feedback';
import Loading from 'Components/Loading';

const { FormikInput, Form, FormContainer } = Formik;

const OptionsLayout = styled(Flex)`
  flex-wrap: wrap;
  gap: 8px;
`;

export const OPTIONS_STATE = {
  DISABLED: 'DISABLED',
  IMPROVEMENT: 'IMPROVEMENT',
  SATISFIED: 'SATISFIED',
};

const Divider = (props) => <Box style={{ borderTop: '1px solid #E5E5E5' }} {...props} />;

// TODO: if selected option is 'other' require values.feedback
function getEnableSubmitForm(optionsState, values) {
  switch (optionsState) {
    case OPTIONS_STATE.DISABLED:
      return false;
    case OPTIONS_STATE.SATISFIED:
      return Array.isArray(values.satisfiedCategory) && values.satisfiedCategory.length > 0;
    case OPTIONS_STATE.IMPROVEMENT:
      return Array.isArray(values.improvementCategory) && values.improvementCategory.length > 0;
    default:
      return false;
  }
}

const FormFields = ({
  formId,
  values,
  setFieldValue,
  closeModal,
  loading,
  title,
  ratings,
  satisfiedLabel,
  satisfiedOptions,
  improvementLabel,
  improvementOptions,
  getOptionState,
}) => {
  const { t } = useTranslation('feedback');

  const optionsState = useMemo(() => getOptionState(values.rating), [values.rating]);

  return (
    <Form id={formId}>
      <Text textStyle={'subtitle1'} textAlign="center" pb={4}>
        {title}
      </Text>
      <FeedbackRating
        name="rating"
        value={values['rating']}
        ratings={ratings}
        onClick={(value) => setFieldValue('rating', value)}
      ></FeedbackRating>
      {optionsState === OPTIONS_STATE.DISABLED ? (
        <>
          <Divider mt={4} mb={2} />
          <Flex justifyContent={'flex-end'}>
            <ButtonText
              py={2}
              px={0}
              minWidth={'60px'}
              width={{ _: 1, sm: 'auto' }}
              onClick={closeModal}
            >
              {t('IMPROVEMENT_AND_SATISFIED.LATER')}
            </ButtonText>
          </Flex>
        </>
      ) : (
        <>
          <Divider my={4} />
          {optionsState === OPTIONS_STATE.SATISFIED && (
            <FormikInput.CheckboxInput
              name="satisfiedCategory"
              labelProps={{ fontWeight: 'semibold', mb: 2 }}
              label={satisfiedLabel}
              options={satisfiedOptions}
              optionContainer={OptionsLayout}
              renderItem={(props) => <FeedbackButtonOption {...props} />}
            />
          )}
          {optionsState === OPTIONS_STATE.IMPROVEMENT && (
            <FormikInput.CheckboxInput
              name="improvementCategory"
              labelProps={{ fontWeight: 'semibold', mb: 2 }}
              label={improvementLabel}
              options={improvementOptions}
              optionContainer={OptionsLayout}
              renderItem={(props) => <FeedbackButtonOption {...props} />}
            />
          )}
          <Box pb={4} />
          <FormikInput.TextArea
            rows={3}
            rowsMax={3}
            name="feedback"
            placeholder={t('IMPROVEMENT_AND_SATISFIED.FREETEXT_PLACEHOLDER')}
          />
          <Flex
            mt={4}
            flexDirection={{ _: 'column', sm: 'row-reverse' }}
            justifyContent={{ _: 'stretch', sm: 'flex-start' }}
          >
            <ButtonFilled
              order="1"
              type="submit"
              borderRadius="8px"
              disabled={!getEnableSubmitForm(optionsState, values) || loading}
            >
              {loading ? <Loading width={20} height={20} /> : t('IMPROVEMENT_AND_SATISFIED.SUBMIT')}
            </ButtonFilled>
            <ButtonText mt={{ _: 2, sm: 0 }} py={2} onClick={closeModal}>
              {t('IMPROVEMENT_AND_SATISFIED.CANCEL')}
            </ButtonText>
          </Flex>
        </>
      )}
    </Form>
  );
};

FormFields.propTypes = {
  formId: PropTypes.string,
  values: PropTypes.object,
  setFieldValue: PropTypes.func,
  closeModal: PropTypes.func,
  loading: PropTypes.bool,
  title: PropTypes.string.isRequired,
  ratings: PropTypes.arrayOf(PropTypes.number).isRequired,
  satisfiedLabel: PropTypes.string.isRequired,
  satisfiedOptions: PropTypes.arrayOf(
    PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })
  ).isRequired,
  improvementLabel: PropTypes.string.isRequired,
  improvementOptions: PropTypes.arrayOf(
    PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })
  ).isRequired,
  getOptionState: PropTypes.func,
};

const FeedbackContainer = ({
  isOpen,
  isSuccess,
  onSubmit,
  onCloseModal,
  onOpenModal,
  ...props
}) => {
  return (
    <MUIModal
      open={isOpen}
      modalStyle={{ padding: '32px', borderRadius: '16px', maxWidth: '520px' }}
      onOpenModal={onOpenModal}
      onCloseModal={onCloseModal}
      closeIcon={isSuccess}
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      {(openModal, closeModal) => {
        if (isSuccess) {
          return <FeedbackComplete />;
        }
        return (
          <FormContainer
            onSubmit={onSubmit}
            initialValues={{
              rating: null,
              improvementOptions: [],
              satisfiedCategory: [],
              feedback: null,
            }}
            validate={() => {}}
            component={(formProps) => (
              <FormFields closeModal={closeModal} {...props} {...formProps} />
            )}
          />
        );
      }}
    </MUIModal>
  );
};

FeedbackContainer.propTypes = {
  isOpen: PropTypes.bool,
  isSuccess: PropTypes.bool,
  formId: PropTypes.string,
  loading: PropTypes.bool,
  title: PropTypes.string.isRequired,
  ratings: PropTypes.arrayOf(PropTypes.number).isRequired,
  satisfiedLabel: PropTypes.string.isRequired,
  satisfiedOptions: PropTypes.arrayOf(
    PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })
  ).isRequired,
  improvementLabel: PropTypes.string.isRequired,
  improvementOptions: PropTypes.arrayOf(
    PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })
  ).isRequired,
  getOptionState: PropTypes.func,
  onSubmit: PropTypes.func,
  onCloseModal: PropTypes.func,
  onOpenModal: PropTypes.func,
};

FeedbackContainer.defaultProps = {
  onSubmit: () => {},
  onCloseModal: () => {},
  onOpenModal: () => {},
  getOptionState: (values) => {},
};

export default FeedbackContainer;
